import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { bulkUpdate } from 'src/components/DevicesSettings/ApplicationSettings/app_settings_helper';
import { deviceHasSettingTitle } from 'src/helpers/settings/device_has_setting_title';
import type { Device } from 'src/types/Device';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import {
	type IoControlConfig,
	type IoInputConfig,
	isNonEmptyConfig,
} from './io_save_payloads';

export type { IoControlConfig, IoInputConfig };
export { buildIoFieldConfig, isNonEmptyConfig } from './io_save_payloads';
export { deviceHasSettingTitle };

export function deviceHasInputStateSetting(device: Device): boolean {
	if (device.category === 'inputs_reader') return false;
	return (
		deviceHasSettingTitle(device, 'input_state') || deviceHasSettingTitle(device, 'input_output')
	);
}

export function deviceShowsInputStateSetting(device: Device, showAdvanced: boolean): boolean {
	return showAdvanced && deviceHasInputStateSetting(device);
}

export async function sendIoSavePayloads(params: {
	device: Device;
	inputUpdates?: { id: number; config: IoInputConfig }[];
	controlUpdates?: { id: number; method: string; config: IoControlConfig }[];
	inputState?: boolean;
	cachedInputState?: boolean;
}): Promise<{ ok: boolean; results: SettingRequest[] }> {
	const { device, inputUpdates = [], controlUpdates = [], inputState, cachedInputState } = params;
	const results: SettingRequest[] = [];
	const inputStateDirty =
		typeof inputState === 'boolean' && inputState !== cachedInputState;

	const pendingInput = inputUpdates.filter((update) => isNonEmptyConfig(update.config));
	const pendingControl = controlUpdates.filter((update) => isNonEmptyConfig(update.config));

	if (!pendingInput.length && !pendingControl.length && !inputStateDirty) {
		return { ok: true, results };
	}

	for (const update of pendingInput) {
		const res = await gen2Request(device, 'Input.SetConfig', {
			id: update.id,
			config: update.config,
		});
		results.push(res);
		onSettingsResponse(res, device);
		if (!res.isok) {
			return { ok: false, results };
		}
	}

	for (const update of pendingControl) {
		const res = await gen2Request(device, update.method, {
			id: update.id,
			config: update.config,
		});
		results.push(res);
		onSettingsResponse(res, device);
		if (!res.isok) {
			return { ok: false, results };
		}
	}

	if (inputStateDirty) {
		const res = await bulkUpdate(device.id, { input_state: inputState });
		results.push(res as SettingRequest);
		onSettingsResponse(res, device);
		if (!res?.isok) {
			return { ok: false, results };
		}
	}

	return { ok: true, results };
}
